// src/components/dashboard/pages/DeliveryScheduleList/components/DeliveryScheduleTable.jsx
import React from 'react';
import DataTable from 'react-data-table-component';
import { Truck, MapPin, Calendar, Scale, Clock, Pen, Eye, User, TrendingUp } from 'lucide-react';
import { tableStyles } from '../../../../../styles/components/js/tableStyle';
import i18n from '../../../../../i18n';
import { Link } from 'react-router-dom';
import { formatDate } from '../../../../../utils/common';

export class DeliveryScheduleTable extends React.Component {
  constructor() {
    super();
    this.state = {
      copiedId: null
    };
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
  }

  handlePageChange = (page) => {
    if (this.props.onChangePage) {
      this.props.onChangePage(page);
    }
  };

  handleRowsPerPageChange = (newLimit, page) => {
    if (this.props.onChangeRowsPerPage) {
      this.props.onChangeRowsPerPage(newLimit);
    }
  };

  handleRowClick = (row, e) => {
    console.log('Row clicked:', row);

    if (this.props.history && row.id) {
      this.props.history.push(`/delivery-site/edit/${row.id}`);
    }
  };

  handleEdit = (row, e) => {
    e?.stopPropagation();
    if (this.props.history && row.id) {
      this.props.history.push(`/delivery-site/edit/${row.id}`);
    }
  };

  // Helper function to format status badge
  getStatusBadge = (status) => {
    const statusMap = {
      'P': { label: 'Scheduled', color: '#3b82f6', bgColor: '#dbeafe' },
      'T': { label: 'In Transit', color: '#8b5cf6', bgColor: '#f3e8ff' },
      'C': { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' },
      'D': { label: 'Delayed', color: '#f59e0b', bgColor: '#fef3c7' }
    };

    const statusInfo = statusMap[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };

    return (
      <span style={{
        backgroundColor: statusInfo.bgColor,
        color: statusInfo.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}>
        <span style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: statusInfo.color,
        }} />
        {statusInfo.label}
      </span>
    );
  };

  // Helper function to get completion status badge
  getCompletionStatusBadge = (progress) => {
    let statusInfo;
    
    if (progress >= 100) {
      statusInfo = { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' };
    } else if (progress >= 75) {
      statusInfo = { label: 'Almost Complete', color: '#8b5cf6', bgColor: '#f3e8ff' };
    } else if (progress >= 50) {
      statusInfo = { label: 'Halfway', color: '#3b82f6', bgColor: '#dbeafe' };
    } else if (progress > 0) {
      statusInfo = { label: 'In Progress', color: '#f59e0b', bgColor: '#fef3c7' };
    } else {
      statusInfo = { label: 'Not Started', color: '#6b7280', bgColor: '#f3f4f6' };
    }

    return (
      <span style={{
        backgroundColor: statusInfo.bgColor,
        color: statusInfo.color,
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}>
        {statusInfo.label}
      </span>
    );
  };

  // Format date for display
  formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  formatWeight = (weight) => {
    return parseFloat(weight || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  getColumns() {
    return [
      {
        name: '#',
        selector: (row, index) => index + 1,
        width: '60px',
        center: true,
      },
      {
        name: '',
        center: true,
        width: '70px',
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Link
              className="btn-new btn-sm"
              to={`/delivery-site/edit/${row.id}`}
              onClick={(e) => this.handleEdit(row, e)}
            >
              <Eye size={14} />
            </Link>
          </div>
        ),
      },
      {
        name: 'Project Name',
        selector: row => row.delivery_schedule?.project_name || '-',
        sortable: true,
        width: '180px',
        cell: row => (
          <Link
            to={`/delivery-site/edit/${row.id}`} 
            onClick={(e) => this.handleEdit(row, e)}
          >
            <div style={{ fontWeight: '600', color: '#1e40af' }}>
              {row.delivery_schedule?.project_name || '-'}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              Lot: {row.lot_ref || '-'}
            </div>
          </Link>
        ),
      },
      {
        name: 'Supervisor',
        selector: row => row.supervisor?.name || row.supervisor_name || '-',
        sortable: true,
        width: '130px',
        cell: row => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={14} color="#6b7280" />
            <span style={{ fontSize: '13px' }}>
              {row.supervisor?.name || row.supervisor_name || '-'}
            </span>
          </div>
        ),
      },
      {
        name: 'Area',
        selector: row => row.area_place?.place_name || '-',
        sortable: true,
        width: '120px',
        cell: row => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={14} color="#6b7280" />
            <span style={{ fontSize: '13px' }}>
              {row.area_place?.place_name || '-'}
            </span>
          </div>
        ),
      },
      {
        name: 'Weight Progress',
        selector: row => row.progress || 0,
        sortable: true,
        width: '200px',
        cell: row => {
          const progress = parseFloat(row.progress) || 0;
          const loadedWeight = parseFloat(row.loaded_weight) || 0;
          const expectedWeight = parseFloat(row.expected_weight) || 0;
          const remainingWeight = parseFloat(row.remaining_weight) || 0;

          return (
            <div className="weight-progress-container">
              {/* Progress bar */}
              <div className="progress-header" style={{ marginBottom: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                    {progress.toFixed(1)}%
                  </span>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {this.formatWeight(loadedWeight)} / {this.formatWeight(expectedWeight)} T
                  </span>
                </div>
              </div>
              
              <div className="progress-bar-container" style={{ marginBottom: '6px' }}>
                <div 
                  style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div 
                    style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: progress >= 100 ? '#10b981' : 
                                      progress >= 75 ? '#8b5cf6' : 
                                      progress >= 50 ? '#3b82f6' : '#f59e0b',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              {/* Status and remaining */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {this.getCompletionStatusBadge(progress)}
                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                  {remainingWeight > 0 ? `${this.formatWeight(remainingWeight)} T left` : 'Complete'}
                </div>
              </div>
            </div>
          );
        },
      },
     
      {
        name: 'Load From/To',
        width: '120px',
        cell: row => (
          <div style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>From:</div>
              <div style={{ fontWeight: '500' }}>{row.load_from || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>To:</div>
              <div style={{ fontWeight: '500' }}>{row.load_to || '-'}</div>
            </div>
          </div>
        ),
      },
      {
        name: 'Status',
        selector: row => row.status,
        sortable: true,
        width: '120px',
        cell: row => this.getStatusBadge(row.status),
      },
      {
        name: 'Dates',
        width: '120px',
        cell: row => (
          <div style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Start:</div>
              <div style={{ fontWeight: '500' }}>
                {this.formatDate(row.expecting_start_date) || '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>End:</div>
              <div style={{ fontWeight: '500' }}>
                {this.formatDate(row.expecting_end_date) || '-'}
              </div>
            </div>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '80px',
        center: true,
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Link
              className="btn-new btn-sm"
              to={`/delivery-site/edit/${row.id}`}
              onClick={(e) => this.handleEdit(row, e)}
            >
              <Pen size={14} />
            </Link>
          </div>
        ),
      },
    ];
  }

  render() {
    const { siteVisits = [], currentPage = 1, limit = 15, total = 0 } = this.props;

    const customTableStyles = {
      ...tableStyles,
      rows: {
        ...tableStyles.rows,
        style: {
          ...tableStyles.rows?.style,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
        },
      },
    };

    return (
      <div className="delivery-schedule-table">
        <DataTable
          key={`${currentPage}-${limit}`}
          title=""
          columns={this.getColumns()}
          data={siteVisits}
          customStyles={customTableStyles}
          highlightOnHover
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationPerPage={limit}
          paginationDefaultPage={currentPage}
          paginationRowsPerPageOptions={[15, 25, 50, 100]}
          paginationComponentOptions={{
            rowsPerPageText: i18n.t('rows_per_page'),
            rangeSeparatorText: i18n.t('out'),
          }}
          onChangeRowsPerPage={this.handleRowsPerPageChange}
          onChangePage={this.handlePageChange}
          onRowClicked={this.handleRowClick}
        />
      </div>
    );
  }
}

export default DeliveryScheduleTable;